import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import pagameService from '../services/pagame.service';

const PagaMe: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const [loading, setLoading] = useState(true);
  const [link, setLink] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Form
  const [valor, setValor] = useState('');
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [mensagem, setMensagem] = useState('');

  useEffect(() => {
    if (code) loadLink();
  }, [code]);

  const loadLink = async () => {
    try {
      setLoading(true);
      const data = await pagameService.getPublicLink(code!);
      setLink(data);
      if (data.valor) setValor(String(data.valor));
    } catch (err: any) {
      setError(err.message || 'Link não encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    const amount = link?.valor || parseFloat(valor);
    if (!amount || amount <= 0) {
      setError('Insere um valor válido');
      return;
    }
    if (!nome.trim()) {
      setError('O nome é obrigatório');
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      setError('Insere um email válido');
      return;
    }
    if (!telefone.trim()) {
      setError('O telemóvel é obrigatório');
      return;
    }
    setError(null);
    setShowConfirmModal(true);
  };

  const handlePay = async () => {
    const amount = link?.valor || parseFloat(valor);
    setPaying(true);
    setError(null);
    try {
      const result = await pagameService.payLink(code!, {
        valor: amount,
        pagador_nome: nome.trim(),
        pagador_email: email.trim(),
        pagador_telefone: telefone.trim(),
        mensagem: mensagem || undefined,
      });
      if (result.paymentUrl) {
        window.location.href = result.paymentUrl;
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao processar pagamento');
      setPaying(false);
      setShowConfirmModal(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent" />
      </div>
    );
  }

  if (error && !link) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">!</span>
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Link indisponível</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-700 to-purple-900 text-white py-6 px-4">
        <div className="max-w-md mx-auto text-center">
          <h1 className="text-2xl font-bold tracking-tight">PagaMe</h1>
          <p className="text-purple-200 text-sm mt-1">Pagamento seguro via Pagali</p>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 -mt-4">
        {/* Payment Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Criador Info */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-xl font-bold text-purple-600">
                  {(link?.criadorNome || 'P').charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="font-semibold text-gray-800">{link?.criadorNome || 'Utilizador Pagali'}</p>
                <p className="text-sm text-gray-500">pediu um pagamento</p>
              </div>
            </div>

            <div className="bg-purple-50 rounded-xl p-4">
              <p className="text-sm text-purple-600 font-medium mb-1">Descrição</p>
              <p className="text-lg font-semibold text-gray-800">{link?.descricao}</p>
            </div>

            {link?.valor && (
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-500">Valor a pagar</p>
                <p className="text-4xl font-bold text-gray-900 mt-1">
                  {link.valor.toLocaleString()}<span className="text-lg">$00</span>
                </p>
                <p className="text-sm text-gray-400">CVE (Escudos)</p>
              </div>
            )}
          </div>

          {/* Form */}
          <div className="p-6 space-y-4">
            {/* Valor (se aberto) */}
            {link?.valorAberto && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor (CVE) *</label>
                <input
                  type="number"
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none text-lg"
                  placeholder="Ex: 1000"
                  min={link?.valorMinimo || 1}
                  max={link?.valorMaximo || undefined}
                />
                {link?.valorMinimo && <p className="text-xs text-gray-400 mt-1">Mínimo: {link.valorMinimo}$00</p>}
              </div>
            )}

            {/* Dados do pagador */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">O teu nome *</label>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none"
                placeholder="Nome completo"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none"
                placeholder="email@exemplo.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telemóvel *</label>
              <input
                type="tel"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none"
                placeholder="Ex: 9XXXXXXX"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem (opcional)</label>
              <input
                type="text"
                value={mensagem}
                onChange={(e) => setMensagem(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none"
                placeholder="Ex: Obrigado pelo almoço!"
                maxLength={255}
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 text-sm rounded-xl p-3">
                {error}
              </div>
            )}

            {/* Confirm Button */}
            <button
              onClick={handleConfirm}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-800 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-purple-900 transition-all text-lg shadow-lg shadow-purple-500/25"
            >
              {`Confirmar ${link?.valor ? `${link.valor.toLocaleString()}$00` : valor ? `${parseFloat(valor).toLocaleString()}$00` : ''}`}
            </button>
          </div>

          {/* Footer */}
          <div className="px-6 pb-6">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>Pagamento seguro via SISP Vinti4</span>
            </div>
            <div className="text-center mt-2">
              <span className="text-xs text-gray-300">Powered by Pagali</span>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-900 px-6 py-5 text-white relative">
              <button
                onClick={() => !paying && setShowConfirmModal(false)}
                disabled={paying}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Fechar"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h2 className="text-xl font-bold">Confirmar Pagamento</h2>
              <p className="text-sm opacity-90 mt-1">Leia atentamente antes de prosseguir</p>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              {/* Amount */}
              <div className="text-center bg-purple-50 rounded-xl p-4">
                <p className="text-sm text-gray-500">Montante a pagar</p>
                <p className="text-3xl font-bold text-purple-700 mt-1">
                  {(link?.valor || parseFloat(valor)).toLocaleString()}$00 <span className="text-lg">CVE</span>
                </p>
              </div>

              {/* Explanation */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-2.5">
                <p className="text-sm font-semibold text-blue-900">O que vai acontecer:</p>
                <div className="space-y-2 text-sm text-blue-800">
                  <div className="flex gap-2">
                    <span className="font-bold">1.</span>
                    <p>Sera redirecionado para a pagina segura do <strong>SISP Vinti4</strong> para inserir os dados do seu cartao</p>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-bold">2.</span>
                    <p>O valor de <strong>{(link?.valor || parseFloat(valor)).toLocaleString()}$00 ECV</strong> sera debitado do seu cartao</p>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-bold">3.</span>
                    <p>O montante sera creditado na wallet Pagali de <strong>{link?.criadorNome}</strong></p>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-bold">4.</span>
                    <p>Recebera confirmacao no email <strong>{email}</strong></p>
                  </div>
                </div>
              </div>

              {/* Payer info summary */}
              <div className="border border-gray-200 rounded-xl p-3 text-xs text-gray-600 space-y-1">
                <div className="flex justify-between"><span>Nome:</span><span className="font-medium text-gray-800">{nome}</span></div>
                <div className="flex justify-between"><span>Email:</span><span className="font-medium text-gray-800">{email}</span></div>
                <div className="flex justify-between"><span>Telemovel:</span><span className="font-medium text-gray-800">{telefone}</span></div>
              </div>

              {error && (
                <div className="bg-red-50 text-red-700 text-sm rounded-xl p-3">{error}</div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  disabled={paying}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
                <button
                  onClick={handlePay}
                  disabled={paying}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-purple-800 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-purple-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/25"
                >
                  {paying ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      A processar...
                    </span>
                  ) : (
                    'Pagar'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PagaMe;
